// Diálogo de criação de white label do merchant logado.
//
// O storeId (loja dona) NÃO está no formulário: o gateway injeta o do token. O
// operador só descreve o white label. O logo é obrigatório na criação (o backend
// exige) — diferente da edição, onde ausência mantém o atual.
//
// O slug é derivado do nome enquanto o operador não o edita à mão: é o
// identificador da URL e digitá-lo do zero convida a erro (espaço, acento).
import { zodResolver } from "@hookform/resolvers/zod"
import { UploadCloudIcon } from "lucide-react"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { z } from "zod"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  FileInput,
  FileUploader,
  FileUploaderContent,
  FileUploaderItem,
} from "@/components/ui/file-upload"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { useCreateAdminWhiteLabelMutation } from "@/services/admin/white-labels"

const panelPrefix = "pages.adminWhiteLabels"

// Mesmo teto da edição (theme-tab): 1 MB.
const LOGO_MAX_SIZE_BYTES = 1024 * 1024

// Reaproveita o padrão do theme-tab (backend aceita data-URI).
const readFileAsDataUri = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () =>
      resolve(String(reader.result ?? ""))
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })

// Nome → slug: minúsculo, sem acento, espaços viram hífen.
const slugify = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")

const schema = z.object({
  name: z.string().min(1).max(255),
  description: z.string().min(1).max(255),
  slug: z
    .string()
    .min(1)
    .max(50)
    // Mesmo formato que o backend guarda: sem espaço/acento/maiúscula.
    .regex(/^[a-z0-9-]+$/, "invalid"),
  principal: z.boolean(),
})

type CreateWhiteLabelFormValues = z.infer<typeof schema>

type CreateWhiteLabelDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export const CreateWhiteLabelDialog = ({
  open,
  onOpenChange,
}: CreateWhiteLabelDialogProps) => {
  const { t } = useTranslation()
  const createMutation = useCreateAdminWhiteLabelMutation()

  const [logoFiles, setLogoFiles] = useState<File[] | null>(
    null
  )
  const [logoDataUri, setLogoDataUri] = useState<string>("")
  const [logoError, setLogoError] = useState<string>("")
  // Slug segue o nome até o operador editá-lo — daí para de sobrescrever.
  const [slugEdited, setSlugEdited] = useState(false)

  const form = useForm<CreateWhiteLabelFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      description: "",
      slug: "",
      principal: false,
    },
  })

  const resetAndClose = () => {
    form.reset()
    setLogoFiles(null)
    setLogoDataUri("")
    setLogoError("")
    setSlugEdited(false)
    onOpenChange(false)
  }

  const handleLogoChange = async (files: File[] | null) => {
    setLogoFiles(files)
    setLogoError("")
    const file = files?.[0]
    if (!file) {
      setLogoDataUri("")
      return
    }
    try {
      setLogoDataUri(await readFileAsDataUri(file))
    } catch {
      setLogoDataUri("")
      setLogoError(t(`${panelPrefix}.create.logoReadError`))
    }
  }

  const handleSubmit = form.handleSubmit((values) => {
    // Logo é obrigatório na criação e não vive no schema (é File, não texto):
    // valida à parte para não deixar submeter sem imagem.
    if (!logoDataUri) {
      setLogoError(t(`${panelPrefix}.create.logoRequired`))
      return
    }
    createMutation.mutate(
      {
        name: values.name,
        description: values.description,
        slug: values.slug,
        logo: logoDataUri,
        principal: values.principal,
      },
      { onSuccess: resetAndClose }
    )
  })

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !next && resetAndClose()}
    >
      <DialogContent className="flex max-h-[90vh] flex-col overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {t(`${panelPrefix}.create.title`)}
          </DialogTitle>
          <DialogDescription>
            {t(`${panelPrefix}.create.description`)}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={handleSubmit}
            className="flex flex-col gap-4"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t(`${panelPrefix}.create.name`)}
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      onChange={(event) => {
                        field.onChange(event)
                        if (!slugEdited) {
                          form.setValue(
                            "slug",
                            slugify(event.target.value)
                          )
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="slug"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t(`${panelPrefix}.create.slug`)}
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      className="font-mono"
                      onChange={(event) => {
                        setSlugEdited(true)
                        field.onChange(event)
                      }}
                    />
                  </FormControl>
                  <FormDescription>
                    {t(`${panelPrefix}.create.slugHint`)}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t(
                      `${panelPrefix}.create.descriptionField`
                    )}
                  </FormLabel>
                  <FormControl>
                    <Textarea rows={2} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-2">
              <span className="text-sm font-medium">
                {t(`${panelPrefix}.create.logo`)}
              </span>
              <FileUploader
                value={logoFiles}
                onValueChange={handleLogoChange}
                dropzoneOptions={{
                  accept: {
                    "image/*": [
                      ".jpg",
                      ".jpeg",
                      ".png",
                      ".gif",
                      ".svg",
                    ],
                  },
                  maxFiles: 1,
                  multiple: false,
                  maxSize: LOGO_MAX_SIZE_BYTES,
                }}
              >
                <FileInput className="rounded-lg border border-dashed">
                  <div className="flex flex-col items-center gap-1 py-4 text-center">
                    <UploadCloudIcon
                      className="size-5 text-muted-foreground"
                      aria-hidden="true"
                    />
                    <span className="text-xs text-muted-foreground">
                      {t(
                        `${panelPrefix}.create.logoDropHint`
                      )}
                    </span>
                  </div>
                </FileInput>
                <FileUploaderContent>
                  {logoFiles?.map((file, index) => (
                    <FileUploaderItem
                      key={file.name}
                      index={index}
                    >
                      <span className="truncate text-xs">
                        {file.name}
                      </span>
                    </FileUploaderItem>
                  ))}
                </FileUploaderContent>
              </FileUploader>
              {logoError && (
                <p className="text-xs text-red-600">
                  {logoError}
                </p>
              )}
            </div>

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={resetAndClose}
              >
                {t(`${panelPrefix}.create.cancel`)}
              </Button>
              <Button
                type="submit"
                disabled={createMutation.isPending}
              >
                {t(`${panelPrefix}.create.submit`)}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
